import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { FuncionarioTercDropDown } from "../../../../common/domain/models/funcionario_terc_dropdown";
import { useUserAuthStore } from "../../../../stores/global/userAuth";
import dayjs from "dayjs";
import { initialInspecaoPatrimonioForm, InspecaoPatrimonioForm, InspecaoPatrimonioSchema } from "../schemas/InspecaoPatrimonio_cadastro/inspecao_patrimonio_cadastro";
import { InspecaoPatrimonioModel } from "../../domain/models/inspecao_patrimonio";
import { InspecaoPatrimonioItensModel } from "../../domain/models/Inspecao_patrimonio_itens";
import { ObraChecklistServicoEstatisticas } from "../../../obras/domain/models/obra_checklist_servico_estatiticas";
import { useInspecaoPatrimonioItensListaPagination } from "../hooks/PatrimonioItensInspecao_verificacao/pagination";
import { PatrimonioChecklistInfo } from "../../domain/models/patrimonio_checklist_info";
import { OcorrenciaInspecaoPatrimonioModel } from "../../domain/models/ocorrencia_inspecao_patrimonio";
import { OcorrenciaInspecaoPatrimonioLista } from "../../domain/models/ocorrencia_inspecao_patrimonio_lista";
import { OcorrenciaInspecaoPatrimonioForm } from "../schemas/PatrimonioItensInspecao_verificacao/ocorrencia_inspecao_patrimonio";
import { useOcorrenciaInspecaoPatrimonioPagination } from "../hooks/PatrimonioItensInspecao_verificacao/pagination_ocorrencias";

/**
 * LoadInspecaoPatrimonioItensFuncState
 */

type LoadInspecaoPatrimonioItensFuncState = {
  idInspecaoPatrimonio?: number;

  page?: number;
  rowsPerPage?: number;
};


/**
 * useObraItensChecklistAreaVerificacaoViewModel
 */

export const usePatrimonioItensInspecaoVerificacaoViewModel = () => {
  const location = useLocation();
  const { idUsuario } = useUserAuthStore();
  const {
    commons: {
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
      getFuncionarioByIdUsuarioUseCase,
    },
    controlePatrimonio: {
      getInspecaoPatrimonioByIdUseCase,
      getAllInspecaoPatrimonioItensUseCase,
      updateInspecaoPatrimonioUseCase,
      getPatrimonioChecklistInfoUseCase,
      getAllOcorrenciaInspecaoPatrimonioUseCase,
      deleteOcorrenciaInspecaoPatrimonioUseCase,
      updateOcorrenciaInspecaoPatrimonioUseCase,
      createOcorrenciaInspecaoPatrimonioUseCase,
      getOcorrenciaInspecaoPatrimonioByIdUseCase,
      downloadOcorrenciaInspecaoPatrimonioArquivoUseCase,

    }

  } = useDependencyInjection();

  const {
    loadFuncionariosDropDown,
    loadFornecedoresDropDown,
    loadFuncionariosTercDropDown,
  } = useDropDownLoader();

  const {
    pushInfoNotification,
    pushErrorNotification,
    setCustomLoading,
  } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const hookForm = useForm<InspecaoPatrimonioForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { ...initialInspecaoPatrimonioForm },
    resolver: zodResolver(InspecaoPatrimonioSchema),
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [fillCurrentDate, setFillCurrentDate] = useState(false);

  const [lockFuncionarioInspecionado, setLockFuncionarioInspecionado] = useState(false);
  const [lockFuncionarioAprovado, setLockFuncionarioAprovado] = useState(false);

  const [funcionariosDropDown, setFuncionariosDropDown] = useState<FuncionarioDropDown[]>();
  const [fornecedoresDropDown, setFornecedoresDropDown] = useState<FornecedorDropDown[]>();
  const [funcionariosTercDropDown, setFuncionariosTercDropDown] = useState<FuncionarioTercDropDown[]>();

  const [idPatrimonioCentroCusto, setIdPatrimonioCentroCusto] = useState<number>();
  const [idPatrimonioMaquinaEquip, setIdPatrimonioMaquinaEquip] = useState<number>();
  const [idInspecaoPatrimonio, setIdInspecaoPatrimonio] = useState<number>();

  const [inspecaoPatrimonio, setInspecaoPatrimonio] = useState<InspecaoPatrimonioModel>();
  const [inspecaoPatrimonioItensLista, setInspecaoPatrimonioItensLista] = useState<InspecaoPatrimonioItensModel[]>();

  const [ocorrenciaModal, setOcorrenciaModal] = useState(false);
  const [deleteOcorrenciaModal, setDeleteOcorrenciaModal] = useState(false);

  const [selectedOcorrencia, setSelectedOcorrencia] = useState<OcorrenciaInspecaoPatrimonioModel>();
  const [selectedIdOcorrencia, setSelectedIdOcorrencia] = useState<number | undefined>();
  const [ocorrenciasLista, setOcorrenciasLista] = useState<OcorrenciaInspecaoPatrimonioLista[]>([]);

  const [selectedInspecaoPatrimonioItem, setSelectedInspecaoPatrimonioItem] = useState<InspecaoPatrimonioItensModel>();


  const [estatisticasChecklistServico, setEstatisticasChecklistServico] = useState<ObraChecklistServicoEstatisticas>();
  const [patrimonioChecklistInfo, setPatrimonioChecklistInfo] = useState<PatrimonioChecklistInfo>();


  const pagination = useInspecaoPatrimonioItensListaPagination();
  const paginationOcorrencias = useOcorrenciaInspecaoPatrimonioPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase,
    onSetError: e => pushErrorNotification("Erro", [e]),
  });

  const [inspecaoPatrimonioItensListaHookForm] = hookForm.watch(['inspecaoPatrimonioItens']);

  const filteredFuncionariosDropdown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter(x => x.canBeSelected);
  }, [
    funcionariosDropDown,
  ]);

  const lockInspecaoObraItens = useMemo(() => {
    return inspecaoPatrimonio && inspecaoPatrimonio.status !== 0 && inspecaoPatrimonio.status !== 3;
  }, [
    inspecaoPatrimonio,
  ]);

  /**
   * Funcoes de carregamento
   */

  const loadPatrimonioChecklistInfo = useCallback(async (idPatrimonioCentroCusto: number, idPatrimonioMaquinaEquip?: number, idInspecaoPatrimonio?: number) => {
    try {
      const response = await getPatrimonioChecklistInfoUseCase.getPatrimonioChecklistInfo(
        idPatrimonioCentroCusto,
        idPatrimonioMaquinaEquip,
        idInspecaoPatrimonio,
      );
      setPatrimonioChecklistInfo(response);
    } catch (e: unknown) {
      throw new Error("Falha ao carregar informações do checklist do patrimônio!");
    }
  }, [
    getPatrimonioChecklistInfoUseCase,
  ]);


  const loadObraChecklistServicoEstatisticas = useCallback(async (id?: number) => {
    try {

      setEstatisticasChecklistServico(undefined);
    } catch (e: unknown) {
      setEstatisticasChecklistServico(undefined);
      throw new Error("Falha ao buscar dados da pagina!");
    }
  }, [
  ]);

  const loadInspecaoPatrimonio = useCallback(async (id?: number) => {
    if (idUsuario) {
      try {
        const filteredIdInspecaoPatrimonio = id ?? idInspecaoPatrimonio;
        const result = await getInspecaoPatrimonioByIdUseCase.getInspecaoPatrimonioById(filteredIdInspecaoPatrimonio ?? 0);
        await getAllInspecaoPatrimonioItensUseCase.getAllInspecaoPatrimonioItens(
          filteredIdInspecaoPatrimonio ?? 0,
          null,
          pagination.page,
          pagination.rowsPerPage,
        );

        const itens = getAllInspecaoPatrimonioItensUseCase.items;
        setInspecaoPatrimonioItensLista(itens);

        const funcionarioResult = await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(idUsuario);
        setLockFuncionarioInspecionado(result.status === 1 || result.status === 2);
        setLockFuncionarioAprovado(result.status === 1 || result.status === 2);

        hookForm.setValue('dataInspecao', result.dataInspecao ? dayjs(result.dataInspecao).toDate() : null);
        hookForm.setValue('idFuncionarioAprovado', result.idFuncionarioAprovado);
        hookForm.setValue('status', result.status ?? 0);
        hookForm.setValue('dataEncerramento', result.dataEncerramento ? dayjs(result.dataEncerramento).toDate() : null);
        hookForm.setValue('observacao', result.observacao);

        hookForm.setValue('inspecaoPatrimonioItens', itens.map(i => ({
          idInspecaoPatrimonioItens: i.idInspecaoPatrimonioItens,
          descricao: i.descricao ?? '',
          inspecao1: i.inspecao1 ?? '',
          dataInspecao1: i.dataInspecao1 ? dayjs(i.dataInspecao1).toDate() : null,
          inspecao2: i.inspecao2 ?? '',
          dataInspecao2: i.dataInspecao2 ? dayjs(i.dataInspecao2).toDate() : null,
        })));

        if (result.idFuncionarioInspecionado) {
          hookForm.setValue('idFuncionarioInspecionado', result.idFuncionarioInspecionado);
        } else {
          if (funcionarioResult && funcionarioResult.idFuncionario) {
            hookForm.setValue('idFuncionarioInspecionado', funcionarioResult.idFuncionario);
          }
        }

        setInspecaoPatrimonio(result);
      } catch (e: unknown) {
        setInspecaoPatrimonio(undefined);
        throw new Error('Falha ao buscar inspeção patrimônio!');
      }
    }
  }, [
    idUsuario,
    idInspecaoPatrimonio,
    getInspecaoPatrimonioByIdUseCase,
    getFuncionarioByIdUsuarioUseCase,
    getAllInspecaoPatrimonioItensUseCase,
    hookForm,
    pagination
  ]);

  const loadInspecaoPatrimonioItens = useCallback(async (idInspecaoPatrimonioParam?: number) => {
    try {
      const filteredIdInspecaoPatrimonio = idInspecaoPatrimonioParam ?? idInspecaoPatrimonio;
      const filteredPage = pagination.page;
      const filteredRowsPerPage = pagination.rowsPerPage;

      await getAllInspecaoPatrimonioItensUseCase.getAllInspecaoPatrimonioItens(
        filteredIdInspecaoPatrimonio ?? 0,
        null,
        filteredPage,
        filteredRowsPerPage,
      );

      hookForm.setValue('inspecaoPatrimonioItens', getAllInspecaoPatrimonioItensUseCase.items.map(x => ({
        idInspecaoPatrimonioItens: x.idInspecaoPatrimonioItens,
        descricao: x.descricao ?? '',
        inspecao1: x.inspecao1 ?? '',
        dataInspecao1: x.dataInspecao1 ? dayjs(x.dataInspecao1).toDate() : null,
        inspecao2: x.inspecao2 ?? '',
        dataInspecao2: x.dataInspecao2 ? dayjs(x.dataInspecao2).toDate() : null,
      })));

      setInspecaoPatrimonioItensLista(getAllInspecaoPatrimonioItensUseCase.items);
      pagination.setTotalItems(getAllInspecaoPatrimonioItensUseCase.totalItems);
    } catch (e: unknown) {
      setInspecaoPatrimonioItensLista(undefined);
      hookForm.setValue('inspecaoPatrimonioItens', []);
      throw new Error('Falha ao buscar itens de inspeção de patrimônio!');
    }
  }, [
    hookForm,
    idInspecaoPatrimonio,
    pagination,
    getAllInspecaoPatrimonioItensUseCase,
  ]);

  const loadListaOcorrencias = useCallback(async (idInspecaoPatrimonio: number) => {
    try {
      await getAllOcorrenciaInspecaoPatrimonioUseCase.getAllOcorrenciaInspecaoPatrimonio(
        idInspecaoPatrimonio,
      );
      setOcorrenciasLista(getAllOcorrenciaInspecaoPatrimonioUseCase.items);
    } catch (e: unknown) {
      setOcorrenciasLista([]);
      throw new Error("Falha ao buscar dados da pagina!");
    }
  }, [getAllOcorrenciaInspecaoPatrimonioUseCase]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locIdPatrimonioCentro = params.get('id');
      const locationPatrimonioMaquinaEquipId = params.get('idPatrimonioMaquinaEquip');
      const locationInspecaoPatrimonioId = params.get('idInspecaoPatrimonio');

      if (!locIdPatrimonioCentro || locIdPatrimonioCentro.length === 0) {
        setErrorMessage("Falha ao recuperar id do centro de custo do patrimônio!");
        return;
      }

      if (!locationPatrimonioMaquinaEquipId || locationPatrimonioMaquinaEquipId.length === 0) {
        setErrorMessage("Falha ao recuperar id do patrimônio máquina/equipamento!");
        return;
      }

      if (!locationInspecaoPatrimonioId || locationInspecaoPatrimonioId.length === 0) {
        setErrorMessage("Falha ao recuperar id de inspeção do patrimônio!");
        return;
      }

      setIdInspecaoPatrimonio(Number(locationInspecaoPatrimonioId));
      setIdPatrimonioCentroCusto(Number(locIdPatrimonioCentro));
      setIdPatrimonioMaquinaEquip(Number(locationPatrimonioMaquinaEquipId));

      await loadFuncionariosDropDown(dt => setFuncionariosDropDown(dt), { includeCantBeSelected: true });

      await loadFornecedoresDropDown(dt => setFornecedoresDropDown(dt));

      await loadFuncionariosTercDropDown(dt => setFuncionariosTercDropDown(dt));

      await loadPatrimonioChecklistInfo(Number(locIdPatrimonioCentro), Number(locationPatrimonioMaquinaEquipId), Number(locationInspecaoPatrimonioId));

      await loadListaOcorrencias(Number(locationInspecaoPatrimonioId));

      await loadObraChecklistServicoEstatisticas(0);

      await loadInspecaoPatrimonio(Number(locationInspecaoPatrimonioId));

      await loadInspecaoPatrimonioItens(Number(locationInspecaoPatrimonioId));


    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados das paginas!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: `/PatrimonioItensInspecao_cadastro?id=${idPatrimonioCentroCusto}&ididPatrimonioMaquinaEquip=${idPatrimonioMaquinaEquip}` });
  }, [
    idPatrimonioMaquinaEquip,
    idPatrimonioCentroCusto,
    backOrNavigate,
  ]);

  const handleReload = useCallback(async () => {
    try {
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados das paginas!'));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSubmit = useCallback(async (form: InspecaoPatrimonioForm) => {
    if (idInspecaoPatrimonio) {
      try {
        setSaving(true);
        const filteredInspecaoPatrimonioItens = form.inspecaoPatrimonioItens ?? inspecaoPatrimonioItensLista
        const inspecaoIniciada = filteredInspecaoPatrimonioItens.some(item => item.dataInspecao1 !== null || item.inspecao1 !== '');
        const status = (inspecaoIniciada && form.status !== 1) ? 3 : form.status;

        await updateInspecaoPatrimonioUseCase.updateInspecaoPatrimonio(
          idInspecaoPatrimonio!,
          form.idPatrimonioChecklistMaquinaEquip ?? undefined,
          form.idFuncionarioAprovado ?? undefined,
          form.idFuncionarioInspecionado ?? undefined,
          undefined,
          form.dataEncerramento ?? undefined,
          form.descricao ?? undefined,
          form.observacao ?? undefined,
          status ?? undefined,
          filteredInspecaoPatrimonioItens,
          fillCurrentDate
        );

        await loadInspecaoPatrimonio(idInspecaoPatrimonio);
        await loadInspecaoPatrimonioItens(idInspecaoPatrimonio);
        pushInfoNotification("Sucesso", "Informações salvas com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar inspeção obra!"));
      } finally {
        setSaving(false);
      }
    }
  }, [
    idInspecaoPatrimonio,
    updateInspecaoPatrimonioUseCase,
    inspecaoPatrimonioItensLista,
    fillCurrentDate,
    loadInspecaoPatrimonio,
    loadInspecaoPatrimonioItens,
    pushInfoNotification,
    pushErrorNotification,
  ]);


  const handleInspecao1Change = useCallback((idInspecaoPatrimonioItens: number, value: string | number | null) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoPatrimonioItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoPatrimonioItens === idInspecaoPatrimonioItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao1 = value?.toString() ?? '';
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao2 = '';
    }

    hookForm.setValue('inspecaoPatrimonioItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleDataInspecao1Change = useCallback((idInspecaoPatrimonioItens: number, value: Date | null) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoPatrimonioItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoPatrimonioItens === idInspecaoPatrimonioItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].dataInspecao1 = value;
    }

    hookForm.setValue('inspecaoPatrimonioItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleInspecao2Change = useCallback((idInspecaoPatrimonioItens: number, value: string | number | null) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoPatrimonioItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoPatrimonioItens === idInspecaoPatrimonioItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao2 = value?.toString() ?? '';
    }

    hookForm.setValue('inspecaoPatrimonioItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleDataInspecao2Change = useCallback((idInspecaoPatrimonioItens: number, value: Date | null) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoPatrimonioItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoPatrimonioItens === idInspecaoPatrimonioItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].dataInspecao2 = value;
    }
    hookForm.setValue('inspecaoPatrimonioItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);

  const handleClearInspecaoPatrimonioItem = useCallback((idInspecaoPatrimonioItens: number) => {
    const copiaInspecaoObraItens = [...hookForm.getValues().inspecaoPatrimonioItens];
    const inspecaoObraItemIndex = copiaInspecaoObraItens.findIndex(x => x.idInspecaoPatrimonioItens === idInspecaoPatrimonioItens);

    if (inspecaoObraItemIndex >= 0) {
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao1 = '';
      copiaInspecaoObraItens[inspecaoObraItemIndex].dataInspecao1 = null;
      copiaInspecaoObraItens[inspecaoObraItemIndex].inspecao2 = '';
      copiaInspecaoObraItens[inspecaoObraItemIndex].dataInspecao2 = null;
    }
    hookForm.setValue('inspecaoPatrimonioItens', copiaInspecaoObraItens);
  }, [
    hookForm,
  ]);


  //#region Ocorrencias Eventos

  const handleOcorrenciaModalOpenState = useCallback(async (idOcorrencia: number | null, idInspecaoPatrimonioItens: number | null, openState: boolean) => {
    if (openState) {
      if (idOcorrencia) {
        const ocorrencia = await getOcorrenciaInspecaoPatrimonioByIdUseCase.getOcorrenciaInspecaoPatrimonioById(idOcorrencia);
        setSelectedOcorrencia(ocorrencia);
      } else {
        setSelectedOcorrencia(undefined);
      }

      if (idInspecaoPatrimonioItens && inspecaoPatrimonioItensLista) {
        setSelectedInspecaoPatrimonioItem(inspecaoPatrimonioItensLista.find(x => x.idInspecaoPatrimonioItens === idInspecaoPatrimonioItens));
      } else {
        setSelectedInspecaoPatrimonioItem(undefined);
      }

    } else {
      setSelectedOcorrencia(undefined);
      setSelectedInspecaoPatrimonioItem(undefined);
    }

    setOcorrenciaModal(openState);
  }, [
    getOcorrenciaInspecaoPatrimonioByIdUseCase,
    inspecaoPatrimonioItensLista
  ]);


  const handleOcorrenciaSubmit = useCallback(async (form: OcorrenciaInspecaoPatrimonioForm) => {
    if (inspecaoPatrimonio) {
      try {
        setSaving(true);
        if (selectedOcorrencia) {
          await updateOcorrenciaInspecaoPatrimonioUseCase.updateOcorrenciaInspecaoPatrimonio(
            selectedOcorrencia.idOcorrenciaInspecaoPatrimonio,
            form.limparArquivo1 ?? false,
            form.limparArquivo2 ?? false,
            selectedOcorrencia.idInspecaoPatrimonioItens ?? selectedInspecaoPatrimonioItem?.idInspecaoPatrimonioItens ?? inspecaoPatrimonio.idInspecaoPatrimonio,
            form.idInspecaoPatrimonioItens ?? 0,
            form.dataCriacao ?? undefined,
            form.descricao ?? undefined,
            form.inspecao ?? undefined,
            form.arquivo1 ?? undefined,
            form.arquivo2 ?? undefined,
            form.descricaoArquivo1 ?? undefined,
            form.descricaoArquivo2 ?? undefined,
          );
        } else {
          await createOcorrenciaInspecaoPatrimonioUseCase.createOcorrenciaInspecaoPatrimonio(
            inspecaoPatrimonio.idInspecaoPatrimonio,
            form.idInspecaoPatrimonioItens ?? 0,
            form.dataCriacao ?? undefined,
            form.descricao ?? undefined,
            form.inspecao ?? undefined,
            form.arquivo1 ?? undefined,
            form.arquivo2 ?? undefined,
            form.descricaoArquivo1 ?? undefined,
            form.descricaoArquivo2 ?? undefined,
          );
        }
        await loadListaOcorrencias(inspecaoPatrimonio.idInspecaoPatrimonio);
        setOcorrenciaModal(false);
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar ocorrência!"));
      } finally {
        setSelectedOcorrencia(undefined);
        setSaving(false);
      }
    }
  }, [
    inspecaoPatrimonio,
    selectedOcorrencia,
    selectedInspecaoPatrimonioItem,
    createOcorrenciaInspecaoPatrimonioUseCase,
    updateOcorrenciaInspecaoPatrimonioUseCase,
    loadListaOcorrencias,
    pushErrorNotification,
    pushInfoNotification,


  ]);

  const handleDeleteOcorrenciaModalOpenState = useCallback((idOcorrencia: number | null, openState: boolean) => {
    if (openState) {
      if (idOcorrencia && ocorrenciasLista) {
        setSelectedIdOcorrencia(idOcorrencia);
      } else {
        setSelectedIdOcorrencia(undefined);
      }
    }
    setDeleteOcorrenciaModal(openState);
  }, [
    ocorrenciasLista,
  ]);

  const handleDeleteOcorrenciaSubmit = useCallback(async () => {
    if (selectedIdOcorrencia && inspecaoPatrimonio) {
      try {
        setCustomLoading(true, "Excluindo ocorrência...");
        await deleteOcorrenciaInspecaoPatrimonioUseCase.deleteOcorrenciaInspecaoPatrimonio(
          selectedIdOcorrencia
        );
        await loadListaOcorrencias(inspecaoPatrimonio.idInspecaoPatrimonio);
        setDeleteOcorrenciaModal(false);
        setSelectedIdOcorrencia(undefined);
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir ocorrência!'));
      } finally {
        setCustomLoading(false);
        setSelectedIdOcorrencia(undefined);
      }
    }
  }, [
    selectedIdOcorrencia,
    inspecaoPatrimonio,
    deleteOcorrenciaInspecaoPatrimonioUseCase,
    loadListaOcorrencias,
    pushErrorNotification,
    pushInfoNotification,
    setCustomLoading,
  ]);

  const handleDeleteFileOcorrencia = useCallback((nroArquivo: number) => {
    if (selectedOcorrencia) {
      if (nroArquivo === 1) {
        setSelectedOcorrencia({
          ...selectedOcorrencia,
          arquivo1: undefined,
          nomeArquivo1: undefined,
        });
      } else if (nroArquivo === 2) {
        setSelectedOcorrencia({
          ...selectedOcorrencia,
          arquivo2: undefined,
          nomeArquivo2: undefined,
        });
      }
    }
  }, [selectedOcorrencia]);

  const handleDownloadArquivoOcorrencia = useCallback(async (IdOcorrenciaInspecaoPatrimonio: number, nrArquivo: number) => {
    try {
      setCustomLoading(true, "Preparando download...");
      switch (nrArquivo) {
        case 1:
          const nomeArquivo = ocorrenciasLista?.find(arquivo =>
            arquivo.idOcorrenciaInspecaoPatrimonio === IdOcorrenciaInspecaoPatrimonio)?.nomeArquivo1 ?? 'arquivo';
          await downloadOcorrenciaInspecaoPatrimonioArquivoUseCase
            .downloadOcorrenciaInspecaoPatrimonioArquivo(IdOcorrenciaInspecaoPatrimonio, nomeArquivo, nrArquivo);
          return;
        case 2:
          const nomeArquivo2 = ocorrenciasLista?.find(arquivo =>
            arquivo.idOcorrenciaInspecaoPatrimonio === IdOcorrenciaInspecaoPatrimonio)?.nomeArquivo2 ?? 'arquivo';
          await downloadOcorrenciaInspecaoPatrimonioArquivoUseCase
            .downloadOcorrenciaInspecaoPatrimonioArquivo(IdOcorrenciaInspecaoPatrimonio, nomeArquivo2, nrArquivo);
          return;
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao baixar arquivo!"));
    } finally {
      setCustomLoading(false);
    }
  }, [
    downloadOcorrenciaInspecaoPatrimonioArquivoUseCase,
    ocorrenciasLista,
    setCustomLoading,
    pushErrorNotification,
  ]);


  //#endregion

  // const handleOpenChangeHistory = useCallback((
  //   idTabela: number | null,
  //   tipoTabela: LogTipoTabela | null,
  // ) => {
  //   if (idTabela && tipoTabela) {
  //     changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
  //   } else {
  //     if (idInspecaoObra) {
  //       changeHistory.handleOpenChangeHistory(idInspecaoObra, LogTipoTabela.INSPECAO_OBRA, true);
  //     }
  //   }
  // }, [
  //   idInspecaoObra,
  //   changeHistory,
  // ]);

  const handleFillCurrentDateChange = useCallback((value: boolean) => {
    setFillCurrentDate(value);
    var lista = hookForm.getValues('inspecaoPatrimonioItens');
    if (value) {
      hookForm.setValue('inspecaoPatrimonioItens', (lista ?? []).map(x => {
        return ({
          idInspecaoPatrimonioItens: x.idInspecaoPatrimonioItens,
          descricao: x.descricao ?? null,
          inspecao1: x.inspecao1 ?? '',
          dataInspecao1: dayjs(new Date()).startOf('day').utc().toDate(),
          inspecao2: x.inspecao2 ?? '',
          dataInspecao2: x.dataInspecao2 ?? null,
        });
      }));
    } else {
      hookForm.setValue('inspecaoPatrimonioItens', (lista ?? []).map(x => {
        return ({
          idInspecaoPatrimonioItens: x.idInspecaoPatrimonioItens,
          descricao: x.descricao ?? null,
          inspecao1: x.inspecao1 ?? '',
          dataInspecao1: null,
          inspecao2: x.inspecao2 ?? '',
          dataInspecao2: x.dataInspecao2 ?? null,
        });
      }));
    }
  }, [
    hookForm,
  ]);


  return {
    hookForm,
    loading,
    saving,
    errorMessage,
    fillOcorrenciaData: false,
    lockFuncionarioInspecionado,
    lockFuncionarioAprovado,
    funcionariosDropdown: funcionariosDropDown ?? [],
    filteredFuncionariosDropdown: filteredFuncionariosDropdown ?? [],
    fornecedoresDropDown: fornecedoresDropDown ?? [],
    funcionariosTercDropDown: funcionariosTercDropDown ?? [],
    idInspecaoPatrimonio,
    idPatrimonioCentroCusto,
    idPatrimonioMaquinaEquip,
    inspecaoPatrimonio: inspecaoPatrimonio ?? null,
    inspecoesPatrimonioItensLista: inspecaoPatrimonioItensListaHookForm ?? [],
    estatisticasChecklistServico: estatisticasChecklistServico ?? null,
    patrimonioChecklistInfo,
    pagination,
    changeHistory,
    lockInspecaoObraItens,
    ocorrenciaModal,
    deleteOcorrenciaModal,
    ocorrenciasLista,
    paginationOcorrencias,
    selectedOcorrencia,
    selectedInspecaoPatrimonioItem,

    handleOcorrenciaModalOpenState,
    handleBack,
    handleReload,
    handleSubmit: hookForm.handleSubmit(handleSubmit),
    handleInspecao1Change,
    handleDataInspecao1Change,
    handleInspecao2Change,
    handleDataInspecao2Change,
    handleClearInspecaoPatrimonioItem,
    handleFillCurrentDateChange,
    handleOcorrenciaSubmit,
    handleDeleteOcorrenciaModalOpenState,
    handleDeleteOcorrenciaSubmit,
    handleDeleteFileOcorrencia,
    handleDownloadArquivoOcorrencia,
  };
}